import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';
import { FeaturesTreeReaderFactory } from 'types';
import { Credentials, CredentialsFactory, UserFeaturesTemplateProvider, UserProfile, UsersStore } from 'authorization';
import { RequestDataAccessServiceConfig } from '../request-data-access.service';
import { CouchDbRepository } from '../base/couchdb-repository';

@Injectable()
export class CouchDbUsersStore extends UsersStore {
  private _prefix = 'org.couchdb.user:';
  private _repo: CouchDbRepository<UserProfile>;

  constructor(
    private _httpClient: HttpClient,
    private _config: RequestDataAccessServiceConfig,
    private _credentialsFactory: CredentialsFactory,
    private _featuresTemplateProvider: UserFeaturesTemplateProvider,
    private _featuresTreeReaderFactory: FeaturesTreeReaderFactory,
  ) {
    super();
    this._repo = new CouchDbRepository<UserProfile>(
      (x: UserProfile) => {
        return this._prefix + x.login;
      },
      (userDoc: any) => {
        const profile = new UserProfile(userDoc.name);
        profile.lastLogin = !!userDoc.lastLogin ? new Date(userDoc.lastLogin) : new Date(0);
        profile.features = this._featuresTemplateProvider.getUserFeaturesTemplate();
        if (!!userDoc.features) {
          this._featuresTreeReaderFactory.create().readTree(userDoc.features, profile.features);
        }
        return profile;
      },
      '_users',
      this._httpClient,
      this._config,
      this._credentialsFactory,
    );
  }

  getAllUsers(): Observable<UserProfile> {
    return this._repo.getAll$(true).pipe(filter((x) => x.login !== 'guest'));
  }

  private createAuthorizationOptions(credentials: Credentials): any {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + credentials.asEncodedString,
      }),
    };
  }

  addNewUser(login: string, password: string): Observable<boolean> {
    return this._credentialsFactory.getCredentials$().pipe(concatMap((_) => this.internalAddNewUser(_, login, password)));
  }

  internalAddNewUser(credentials: Credentials, login: string, password: string): Observable<boolean> {
    if (login === 'adm' || login === 'guest') {
      throw new Error('Cannot change system user');
    }
    const addUserQueryUrl = this._config.getServerURL() + '_users/' + this._prefix + login;
    const addUserQueryBody = { name: login, roles: ['user'], type: 'user', password: password };
    return this._httpClient.put(addUserQueryUrl, addUserQueryBody, this.createAuthorizationOptions(credentials)).pipe(
      catchError(() => of(false)),
      map(() => true),
    );
  }

  deleteUser(login: string): Observable<boolean> {
    return this._repo.remove$(this._prefix + login);
  }

  changeUserPassword(login: string, newPassword: string): Observable<boolean> {
    return this._credentialsFactory.getCredentials$().pipe(concatMap((_) => this.internalChangeUserPassword(_, login, newPassword)));
  }

  changeAdminPassword(newPassword: string): Observable<boolean> {
    return this._credentialsFactory.getCredentials$().pipe(concatMap((_) => this.internalChangeAdminPassword(_, newPassword)));
  }

  private internalChangeUserPassword(credentials: Credentials, login: string, newPassword: string): Observable<boolean> {
    const url = this._config.getServerURL() + '_users/' + this._prefix + login;
    return this._httpClient.get(url, this.createAuthorizationOptions(credentials)).pipe(
      concatMap((response: any) => {
        const data = response;
        data.password = newPassword;
        return this._httpClient.put(url, data, this.createAuthorizationOptions(credentials)).pipe(
          map((_: any) => {
            return _.ok;
          }),
        );
      }),
    );
  }

  private internalChangeAdminPassword(credentials: Credentials, newPassword: string): Observable<boolean> {
    const url = this._config.getServerURL() + '_membership';
    return this._httpClient.get(url, this.createAuthorizationOptions(credentials)).pipe(
      concatMap((response: any) => {
        const url2 = this._config.getServerURL() + '_node/' + response['all_nodes'][0] + '/_config/admins/adm';
        return this._httpClient.put(url2, '"' + newPassword + '"', this.createAuthorizationOptions(credentials)).pipe(
          map((_: any) => {
            return true; // TODO check errors?
          }),
        );
      }),
    );
  }

  getUser(login: string): Observable<UserProfile | null> {
    return this._repo.get$(this._prefix + login);
  }

  setProfile(profile: UserProfile): Observable<boolean> {
    return this._repo.store$(profile).pipe(map((x) => !!x));
  }

  updateLastLogin(login: string): Observable<boolean> {
    return this._repo.get$(this._prefix + login).pipe(
      concatMap((x) => {
        if (!x) {
          return of(null);
        }
        x.lastLogin = new Date();
        return this._repo.store$(x);
      }),
      map((x) => !!x),
    );
  }
}
